import { LOG_LEVELS } from '@fiverr-private/obs';
import { calculateRollout } from './calculation';
import { setUserConfiguration } from './configuration';
import { Configuration } from './interfaces';
import { validateArgs, validateIdentifier, validateConfiguration } from './helpers/validators';
import { log, count, countDistribution } from './metrics';
import { getRolloutData, initialPopulateCache } from './orchestrator';

/**
 * Sets user defined rollout parameters (throws an exception in case of errors)
 *
 * @param configuration {Configuration}:
 *   - 'appName' [string]: Application name (ex: 'xxx_perseus'). [required]
 *   - 'serviceUrl' [string]: Service env variable. [required]
 *   - 'rollouts' {string[]}: Array of rollouts to fetch in initialization. (optional)
 *   - 'cacheTtl' {number}: cache time to live (ms). Must be greater than 0. Default is 60000ms (1 minute).
 *   - 'fetchTimeout {number}: timeout for fetch from server (ms). Must be >= 0. Default is 1000ms.
 *   - 'refreshTimeout {number}: timeout for refreshes from server (ms). Must be >= 0. Default is 0 meaning, no timeout.
 */
export const initialize = async (configuration: Configuration): Promise<void> => {
    validateConfiguration(configuration);
    setUserConfiguration(configuration);
    await initialPopulateCache(configuration);
};

/**
 * Checks whether or not given identifier is 'in' supplied rollout
 *
 * @param rolloutName {string} - Rollout name
 * @param userIdentifier - {string | number} - user identifier. Either userId or uGuid
 * @param defaultValue {boolean}
 * @returns {boolean} - whether or not the given userIdentifier is in the rollout
 */
export const isActive = async (
    rolloutName: string,
    userIdentifier: number | string,
    defaultValue = false
): Promise<boolean> => {
    validateArgs(rolloutName);

    try {
        validateIdentifier(userIdentifier);
        const { percentage, users, excludedUsers } = await getRolloutData(rolloutName);

        const inRollout = calculateRollout(rolloutName, userIdentifier.toString(), percentage, users, excludedUsers);

        countDistribution(rolloutName, inRollout);

        return inRollout;
    } catch (error) {
        count(`errors.critical.${rolloutName}`);
        log(LOG_LEVELS.ERROR, error as Error, { rolloutName, defaultValue });

        return defaultValue;
    }
};

/**
 * Returns true if rollout is open, else false (does not take identifier into account)
 *
 * @param rolloutName {string} - Rollout name
 * @param defaultValue {boolean} response in case of a failed request.
 * @returns {boolean} - true if and only if the given rollout is at 100%
 */
export const isActiveBinary = async (rolloutName: string, defaultValue = false): Promise<boolean> => {
    validateArgs(rolloutName);

    try {
        const { percentage } = await getRolloutData(rolloutName);

        const inRollout = percentage === 100;

        countDistribution(rolloutName, inRollout);

        return inRollout;
    } catch (error) {
        count(`errors.critical.${rolloutName}`);
        log(LOG_LEVELS.ERROR, error as Error, { rolloutName, defaultValue });

        return defaultValue;
    }
};
